/**
 * Game state construction and derived values.
 */

import type {
  BuildingKey,
  DefenseKey,
  GameState,
  PlanetState,
  ResourceAmounts,
  ShipKey,
  StoredResource,
} from './types.js';
import { BUILDINGS, BUILDING_KEYS } from './data/buildings.js';
import { talentEffects } from './data/talents.js';
import { createMarket } from './market.js';
import { createCasino } from './casino.js';
import { createShop } from './shop.js';
import { computeModifiers } from './modifiers.js';
import {
  BASE_INCOME_SILICATE,
  BASE_INCOME_TITAN,
  BASE_STORAGE,
  energyFactor,
  energyProduction,
  isotopeTempFactor,
  mineEnergyUse,
  mineProduction,
  storageCapacity,
} from './formulas.js';

export const STORED_RESOURCES: StoredResource[] = [
  'titan',
  'silicate',
  'isotope',
  'aether',
  'credit',
  'chronos',
];

const ZERO_BUILDINGS = Object.fromEntries(BUILDING_KEYS.map((k) => [k, 0])) as Record<BuildingKey, number>;

const ZERO_SHIPS: Record<ShipKey, number> = {
  probe: 0, minerShip: 0, transporter: 0, corvette: 0,
  cruiser: 0, battleship: 0, voidPhantom: 0, moonBreaker: 0, colonyShip: 0,
};

const ZERO_DEFENSE: Record<DefenseKey, number> = {
  gauss: 0, plasma: 0, shieldDome: 0, interceptor: 0,
};

export interface NewGameOptions {
  playerName?: string;
  planetName?: string;
  /** Mean planet temperature in °C — colder boosts Isotop-3 (GDD §2). */
  temperature?: number;
  seed?: number;
  now?: number;
  /**
   * Real galaxy coordinates reserved via GalaxyService.assignHomeSlot()
   * (galaxy plan Milestone B). Falls back to the seeded cosmetic values
   * below when absent — used by tests and any direct engine caller with no
   * backend/galaxy behind it.
   */
  homeSlot?: { system: number; position: number };
}

/**
 * A fresh colony in Ring 5, the protected newbie cradle (GDD §3).
 * Starting resources are enough for the first few mine levels — the classic
 * "first session hooks you" opening the GDD's retention loop expects.
 */
export function createNewGame(options: NewGameOptions = {}): GameState {
  const now = options.now ?? Date.now();
  const seed = options.seed ?? (now % 2_147_483_647);
  return {
    version: 1,
    playerName: options.playerName ?? 'Kommandant',
    level: 1,
    xp: 0,
    talentPoints: 0,
    talents: { economy: 0, war: 0, astronomy: 0 },
    resources: { titan: 1_200, silicate: 600, isotope: 0, aether: 0, credit: 500, chronos: 40 },
    planets: [
      {
        name: options.planetName ?? 'Heimatwelt',
        ring: 5,
        system: options.homeSlot?.system ?? 1 + (seed % 400),
        position: options.homeSlot?.position ?? 1 + (seed % 12),
        temperature: options.temperature ?? -10,
        buildings: { ...ZERO_BUILDINGS },
        damaged: [],
        ships: { ...ZERO_SHIPS },
        defense: { ...ZERO_DEFENSE },
        crater: null,
        buildQueue: [],
        shipyardQueue: [],
      },
    ],
    moon: { name: 'Basis-Mond', buildings: { phalanx: 0 } },
    blueprints: {},
    camps: [],
    campsRefreshAt: now,
    fleetMissions: [],
    missionIndex: 0,
    effects: [],
    research: {
      impulseDrive: 0, warpFold: 0, shieldHarmonics: 0, asteroidMining: 0,
      logistics: 0, astrometrics: 0, gravitonPhysics: 0,
    },
    researchQueue: [],
    event: null,
    // First sector event arrives 30–60 min into the game (design fill-in —
    // the 4–8 h GDD cadence starts after the opening session).
    nextEventAt: now + 30 * 60_000 + (seed % (30 * 60_000)),
    debris: { titan: 0, silicate: 0 },
    market: createMarket(now),
    casino: createCasino(now),
    shop: createShop(now),
    rngSeed: seed,
    nextId: 1,
    lastTick: now,
    createdAt: now,
  };
}

/**
 * A fresh, unbuilt colony planet (galaxy plan Milestone E) — same empty
 * shape `createNewGame()` gives the home planet, at real coordinates a
 * `foundColony()` caller (actions.ts) already claimed via the backend.
 */
export function createColonyPlanet(
  name: string,
  ring: PlanetState['ring'],
  system: number,
  position: number,
  temperature: number,
): PlanetState {
  return {
    name,
    ring,
    system,
    position,
    temperature,
    buildings: { ...ZERO_BUILDINGS },
    damaged: [],
    ships: { ...ZERO_SHIPS },
    defense: { ...ZERO_DEFENSE },
    crater: null,
    buildQueue: [],
    shipyardQueue: [],
  };
}

/**
 * Empire-wide research bonus (galaxy plan Milestone E): research labs sum
 * across every owned planet, unlike the `nanoFactory` speed bonus, which
 * remains home-planet-only for both building and shipyard queues.
 */
export function totalResearchLabLevel(state: GameState): number {
  return state.planets.reduce((sum, planet) => sum + planet.buildings.researchLab, 0);
}

/**
 * Level of a building, wherever it stands.
 *
 * Buildings live on a planet by default; the Subraum-Phalanx stands on the
 * base moon (GDD §3). Routing every read through here means callers never have
 * to know which — and moving a building between locations stays a one-line
 * catalogue change. `planetIndex` defaults to 0 (the home planet) — every
 * callers pass a non-zero index when managing a colony.
 */
export function buildingLevel(state: GameState, key: BuildingKey, planetIndex = 0): number {
  return BUILDINGS[key].location === 'moon'
    ? (state.moon.buildings[key] ?? 0)
    : state.planets[planetIndex]!.buildings[key];
}

/** Mutates: set a building's level in whichever location it belongs to. */
export function setBuildingLevel(state: GameState, key: BuildingKey, level: number, planetIndex = 0): void {
  if (BUILDINGS[key].location === 'moon') state.moon.buildings[key] = level;
  else state.planets[planetIndex]!.buildings[key] = level;
}

/** Effective building level: damaged buildings produce nothing (GDD §3). */
function effectiveLevel(state: GameState, key: BuildingKey, planetIndex = 0): number {
  return state.planets[planetIndex]!.damaged.includes(key) ? 0 : buildingLevel(state, key, planetIndex);
}

export interface ProductionReport {
  /** Per-hour rates AFTER energy scaling, talents and crater bonus. */
  perHour: { titan: number; silicate: number; isotope: number };
  energy: { produced: number; consumed: number; factor: number };
}

/**
 * Current production rates. Pure function of state (+ a reference time for the
 * crater bonus) — the UI shows these numbers and the tick integrates them.
 *
 * Mines sum across every owned planet into the single empire-wide resource
 * pool (galaxy plan Milestone E, same "sums across planets" pattern as
 * `totalResearchLabLevel`) — energy stays ONE shared grid (produced/consumed
 * summed first, one brownout `factor` applied everywhere) rather than a
 * separate grid per planet, which would need per-planet energy modelling
 * nothing else in the game has.
 */
export function productionReport(state: GameState, at: number): ProductionReport {
  const mods = computeModifiers(state, at);
  const talents = talentEffects(state.talents);

  let produced = 0;
  let consumed = 0;
  for (let i = 0; i < state.planets.length; i++) {
    produced += energyProduction(effectiveLevel(state, 'solarPlant', i));
    consumed +=
      mineEnergyUse(effectiveLevel(state, 'titanSmelter', i)) +
      mineEnergyUse(effectiveLevel(state, 'silicateRefinery', i)) +
      mineEnergyUse(effectiveLevel(state, 'isotopeCondenser', i));
  }
  produced *= mods.energy;
  const factor = energyFactor(produced, consumed);

  const titanBase = BUILDINGS.titanSmelter.production!.basePerHour;
  const silicateBase = BUILDINGS.silicateRefinery.production!.basePerHour;
  const isotopeBase = BUILDINGS.isotopeCondenser.production!.basePerHour;

  let titan = 0;
  let silicate = 0;
  let isotope = 0;
  for (let i = 0; i < state.planets.length; i++) {
    const planet = state.planets[i]!;
    const craterActive = planet.crater !== null && at < planet.crater.expiresAt;
    const craterFactor = craterActive ? planet.crater!.factor : 1;
    titan += mineProduction(titanBase, effectiveLevel(state, 'titanSmelter', i)) * factor * talents.mineBonus;
    silicate +=
      mineProduction(silicateBase, effectiveLevel(state, 'silicateRefinery', i)) *
      factor * talents.mineBonus * craterFactor;
    isotope +=
      mineProduction(isotopeBase, effectiveLevel(state, 'isotopeCondenser', i)) *
      factor * talents.mineBonus * isotopeTempFactor(planet.temperature);
  }

  return {
    perHour: {
      titan: (BASE_INCOME_TITAN + titan) * mods.production,
      silicate: (BASE_INCOME_SILICATE + silicate) * mods.production,
      isotope: isotope * mods.production,
    },
    energy: { produced, consumed, factor },
  };
}

export interface StorageReport {
  titan: number;
  silicate: number;
  isotope: number;
}

/**
 * Current storage caps. Damaged storage buildings keep their capacity — a
 * comet hit stops *machinery*, it doesn't vaporise the warehouse contents.
 * Sums across every owned planet, same as `productionReport` — a warehouse
 * on any colony grows the one shared empire-wide store.
 */
export function storageReport(state: GameState, at = state.lastTick): StorageReport {
  const mods = computeModifiers(state, at);
  let titan = 0;
  let silicate = 0;
  let isotope = 0;
  for (const planet of state.planets) {
    titan += storageCapacity(BASE_STORAGE.titan, planet.buildings.titanDepot);
    silicate += storageCapacity(BASE_STORAGE.silicate, planet.buildings.silicateVault);
    isotope += storageCapacity(BASE_STORAGE.isotope, planet.buildings.isotopeTank);
  }
  return {
    titan: Math.round(titan * mods.storage),
    silicate: Math.round(silicate * mods.storage),
    isotope: Math.round(isotope * mods.storage),
  };
}

/** Can the player afford this? */
export function canAfford(state: GameState, cost: ResourceAmounts): boolean {
  return STORED_RESOURCES.every((r) => (cost[r] ?? 0) <= state.resources[r]);
}

/** Mutates: subtract cost. Caller must have checked affordability. */
export function payCost(state: GameState, cost: ResourceAmounts): void {
  for (const r of STORED_RESOURCES) {
    const c = cost[r] ?? 0;
    if (c > 0) state.resources[r] = Math.max(0, state.resources[r] - c);
  }
}

/** Mutates: add resources. */
export function addResources(state: GameState, gain: ResourceAmounts): void {
  for (const r of STORED_RESOURCES) {
    const g = gain[r] ?? 0;
    if (g > 0) state.resources[r] += g;
  }
}
